import { Heading, LearnMoreLink, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { useEnv } from '../../../context/env-context';
import { useHandlers, useUser, withPageProviders } from '../../../context/page-context';
import { useSyncApi } from '../../sync/hooks/use-sync-api';
import { useSyncStatus } from '../../sync/hooks/use-sync-log-status';
import type { SyncLogIdentifier } from '../../sync/types';
import { ProductSearch } from '../components/product-search';
import { ProductsSyncLogAlert } from '../components/products-sync-log-alert';
import { DOCS_URLS, I18N_PAGE, PRODUCTS_SYNC_PATH } from '../constants';
import { useProductLookup } from '../hooks/use-product-lookup';

type ProductsSyncPageProps = {
  initialSearchTerm?: string;
};

export const ProductsSyncPage = withPageProviders(
  ({ initialSearchTerm = '' }: ProductsSyncPageProps) => {
    const env = useEnv();
    const { isSparkStaff } = useUser();
    const { navigationHandler, notificationHandler } = useHandlers();
    const { syncApi } = useSyncApi();
    const [isSyncing, setIsSyncing] = useState(false);

    const syncFor: SyncLogIdentifier = { integration: env.platform ?? '', dataType: 'products' };
    // Same query key as ProductsSyncLogAlert, so refetching here updates the banner it renders.
    const { refetch: refetchSyncStatus, isFetching: isRefetchingStatus } = useSyncStatus(syncFor);

    const { searchTerm, debouncedTerm, setSearchTerm, results, isFetching, isError, refetch } =
      useProductLookup(initialSearchTerm);

    const triggerManualSync = () => {
      setIsSyncing(true);
      syncApi
        .triggerManualSync({
          teamSlug: env.team.slugged_name,
          environment: env.environment,
          dataType: syncFor.dataType,
        })
        .then(() => {
          notificationHandler.success(t(`${I18N_PAGE}.notifications.sync_started`));
          return refetchSyncStatus();
        })
        .catch(() => notificationHandler.error())
        .finally(() => setIsSyncing(false));
    };

    return (
      <Page
        title={t(`${I18N_PAGE}.title`)}
        description={t(`${I18N_PAGE}.description`)}
        learnMoreUrl={DOCS_URLS.dataSync}
        primaryAction={
          isSparkStaff
            ? {
                label: t(`${I18N_PAGE}.actions.manual_sync`),
                onClick: triggerManualSync,
                loading: isSyncing,
              }
            : undefined
        }
        secondaryActions={[
          {
            label: t('buttons:reload'),
            onClick: () => void refetchSyncStatus(),
            loading: isRefetchingStatus,
          },
        ]}
      >
        <ProductsSyncLogAlert />

        <div className="flex flex-col gap-1">
          <Heading style="md" level="h2">
            {t(`${I18N_PAGE}.lookup_title`)}
          </Heading>
          <Text variant="secondary">
            {t(`${I18N_PAGE}.lookup_description`)} <LearnMoreLink link={DOCS_URLS.dataSyncLookup} />
          </Text>
        </div>

        <ProductSearch
          searchTerm={searchTerm}
          debouncedTerm={debouncedTerm}
          results={results}
          isFetching={isFetching}
          isError={isError}
          onRetry={refetch}
          platform={env.connected_platform}
          onSearchTermChange={setSearchTerm}
          onSelectVariant={(variantSku) =>
            navigationHandler.navigate(`${PRODUCTS_SYNC_PATH}/${encodeURIComponent(variantSku)}`)
          }
        />
      </Page>
    );
  },
);
